export interface MaterialClass {
  label: { en: string; de: string };
  include: string[];
  exclude: string[];
  synonyms: string[];
}

export const MATERIAL_CLASSES: Record<string, MaterialClass> = {
  concrete: {
    label: { en: 'concrete', de: 'Beton' },
    include: ['beton', 'concrete', 'cast in situ'],
    exclude: ['betonstahl', 'betonoptik', 'block', 'stein', 'paneel'],
    synonyms: ['concrete', 'beton', 'ortbeton', 'stahlbeton', 'kubikmeter beton'],
  },
  masonry: {
    label: { en: 'masonry', de: 'Mauerwerk' },
    include: ['mauerwerk', 'masonry', 'ziegel', 'brick', 'concrete block', 'block'],
    exclude: [],
    synonyms: ['masonry', 'mauerwerk', 'ziegel', 'brick', 'bricks'],
  },
  steel: {
    label: { en: 'steel', de: 'Stahl' },
    include: ['stahl', 'steel', 'baustahl', 'betonstahl'],
    exclude: [],
    synonyms: ['steel', 'stahl', 'baustahl', 'structural steel'],
  },
  timber: {
    label: { en: 'timber', de: 'Holz' },
    include: ['holz', 'wood', 'timber', 'lumber', 'plywood', 'fichte', 'brettsperrholz', 'bsh'],
    exclude: [],
    synonyms: ['timber', 'wood', 'holz'],
  },
  insulation: {
    label: { en: 'insulation', de: 'Dämmung' },
    include: ['dämmung', 'daemmung', 'insulation', 'dämm'],
    exclude: [],
    synonyms: ['insulation', 'dämmung', 'daemmung', 'wärmedämmung'],
  },
  gypsum: {
    label: { en: 'gypsum', de: 'Gipskarton' },
    include: ['gips', 'gypsum', 'plasterboard', 'trockenbau'],
    exclude: [],
    synonyms: ['gypsum', 'plasterboard', 'drywall', 'gips', 'gipskarton', 'trockenbau'],
  },
  glass: {
    label: { en: 'glass', de: 'Glas' },
    include: ['glas', 'glass'],
    exclude: [],
    synonyms: ['glass', 'glas', 'verglasung'],
  },
};

export const TYPE_ALIASES: Record<string, string> = {
  wall: 'IfcWall',
  walls: 'IfcWall',
  wand: 'IfcWall',
  wände: 'IfcWall',
  waende: 'IfcWall',
  slab: 'IfcSlab',
  slabs: 'IfcSlab',
  decke: 'IfcSlab',
  decken: 'IfcSlab',
  geschossdecke: 'IfcSlab',
  bodenplatte: 'IfcSlab',
  floor: 'IfcSlab',
  floors: 'IfcSlab',
  column: 'IfcColumn',
  columns: 'IfcColumn',
  stütze: 'IfcColumn',
  stützen: 'IfcColumn',
  stuetze: 'IfcColumn',
  stuetzen: 'IfcColumn',
  säule: 'IfcColumn',
  beam: 'IfcBeam',
  beams: 'IfcBeam',
  träger: 'IfcBeam',
  traeger: 'IfcBeam',
  balken: 'IfcBeam',
  unterzug: 'IfcBeam',
  unterzüge: 'IfcBeam',
  door: 'IfcDoor',
  doors: 'IfcDoor',
  tür: 'IfcDoor',
  türen: 'IfcDoor',
  tuer: 'IfcDoor',
  tueren: 'IfcDoor',
  window: 'IfcWindow',
  windows: 'IfcWindow',
  fenster: 'IfcWindow',
  room: 'IfcSpace',
  rooms: 'IfcSpace',
  space: 'IfcSpace',
  spaces: 'IfcSpace',
  raum: 'IfcSpace',
  räume: 'IfcSpace',
  raeume: 'IfcSpace',
  zimmer: 'IfcSpace',
  footing: 'IfcFooting',
  footings: 'IfcFooting',
  fundament: 'IfcFooting',
  fundamente: 'IfcFooting',
  stair: 'IfcStair',
  stairs: 'IfcStair',
  treppe: 'IfcStair',
  treppen: 'IfcStair',
  roof: 'IfcRoof',
  dach: 'IfcRoof',
  railing: 'IfcRailing',
  geländer: 'IfcRailing',
  covering: 'IfcCovering',
  ceiling: 'IfcCovering',
  member: 'IfcMember',
  plate: 'IfcPlate',
};

export function typeWords(type: string): { en: string; de: string } {
  const t = type.toUpperCase();
  for (const [word, ifc] of Object.entries(TYPE_ALIASES)) {
    if (!t.startsWith(ifc.toUpperCase())) continue;
    const en = TYPE_WORDS[ifc]?.en ?? word;
    const de = TYPE_WORDS[ifc]?.de ?? word;
    return { en, de };
  }
  return { en: type.replace(/^Ifc/, '').toLowerCase(), de: type.replace(/^Ifc/, '') };
}

const TYPE_WORDS: Record<string, { en: string; de: string }> = {
  IfcWall: { en: 'walls', de: 'Wände' },
  IfcSlab: { en: 'slabs', de: 'Decken' },
  IfcColumn: { en: 'columns', de: 'Stützen' },
  IfcBeam: { en: 'beams', de: 'Träger' },
  IfcDoor: { en: 'doors', de: 'Türen' },
  IfcWindow: { en: 'windows', de: 'Fenster' },
  IfcSpace: { en: 'rooms', de: 'Räume' },
  IfcFooting: { en: 'footings', de: 'Fundamente' },
  IfcStair: { en: 'stairs', de: 'Treppen' },
  IfcRoof: { en: 'roofs', de: 'Dächer' },
  IfcRailing: { en: 'railings', de: 'Geländer' },
  IfcCovering: { en: 'coverings', de: 'Bekleidungen' },
  IfcMember: { en: 'members', de: 'Stäbe' },
  IfcPlate: { en: 'plates', de: 'Platten' },
};

export function normalizeType(input: unknown): string {
  const raw = String(input ?? '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower.startsWith('ifc')) return raw.toUpperCase();
  return (TYPE_ALIASES[lower] ?? raw).toUpperCase();
}

export interface MaterialMatcher {
  label: string;
  words: { en: string; de: string };
  test: (materialName: string) => boolean;
}

export function materialMatcher(query: unknown): MaterialMatcher | null {
  const q = String(query ?? '')
    .trim()
    .toLowerCase();
  if (!q) return null;
  for (const [key, cls] of Object.entries(MATERIAL_CLASSES)) {
    if (key === q || cls.synonyms.includes(q)) {
      return {
        label: key,
        words: cls.label,
        test: (name) => {
          const n = name.toLowerCase();
          return cls.include.some((s) => n.includes(s)) && !cls.exclude.some((s) => n.includes(s));
        },
      };
    }
  }
  return { label: q, words: { en: q, de: q }, test: (name) => name.toLowerCase().includes(q) };
}

export function materialClassIn(text: string): string | null {
  const t = text.toLowerCase();
  for (const [key, cls] of Object.entries(MATERIAL_CLASSES))
    if (cls.synonyms.some((s) => t.includes(s))) return key;
  return null;
}

export function typeIn(text: string): string | null {
  const t = text.toLowerCase();
  const words = Object.keys(TYPE_ALIASES).sort((a, b) => b.length - a.length);
  for (const w of words)
    if (new RegExp(`(^|[^a-zäöü])${w}([^a-zäöü]|$)`).test(t)) return TYPE_ALIASES[w]!;
  return null;
}
